/**
 * Cuenta de Resultados (P&L).
 *
 * Selector trimestre/año + filtro por línea de servicio. Tabla jerárquica con
 * los bloques del PGC (ingresos explotación, aprovisionamientos, personal,
 * otros gastos, financieros, impuestos) y comparativa con el año anterior.
 *
 * ADMIN-only (la API ya valida el rol).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, BookOpen, Loader2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import { useServiceLines } from '../lib/service-lines'
import type { PnlReport, PnlSection } from '@toga/shared'

const CURRENT_YEAR = new Date().getFullYear()

type QuarterMode = 'T1' | 'T2' | 'T3' | 'T4' | 'YEAR'

interface QuarterDef {
  value: QuarterMode
  /** i18n key relative al ns 'accounting'. */
  labelKey: string
  /** Mes 0-indexed. */
  fromMonth: number
  toMonth: number
}

const QUARTERS: QuarterDef[] = [
  { value: 'YEAR', labelKey: 'quarters.year', fromMonth: 0, toMonth: 11 },
  { value: 'T1', labelKey: 'quarters.t1', fromMonth: 0, toMonth: 2 },
  { value: 'T2', labelKey: 'quarters.t2', fromMonth: 3, toMonth: 5 },
  { value: 'T3', labelKey: 'quarters.t3', fromMonth: 6, toMonth: 8 },
  { value: 'T4', labelKey: 'quarters.t4', fromMonth: 9, toMonth: 11 },
]

/** Última fecha del mes (1-indexed mes 1-12). */
function lastDayOfMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate()
}

/** Formato YYYY-MM-DD. */
function fmt(year: number, month0: number, day: number): string {
  return `${year}-${String(month0 + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Bloques cuyo subtotal queremos resaltar (totales/resultados intermedios). */
const HIGHLIGHTED_KEYS: ReadonlySet<PnlSection['key']> = new Set([
  'RESULTADO_EXPLOTACION',
  'RESULTADO_FINANCIERO',
  'RESULTADO_ANTES_IMPUESTOS',
  'RESULTADO_EJERCICIO',
])

export default function AccountingPnl() {
  const { t } = useTranslation('accounting')
  const { lines } = useServiceLines()
  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const [quarter, setQuarter] = useState<QuarterMode>('YEAR')
  const [serviceLine, setServiceLine] = useState<string>('')

  const [report, setReport] = useState<PnlReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const yearOptions = useMemo(() => {
    const arr: number[] = []
    for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 6; y--) arr.push(y)
    return arr
  }, [])

  const { from, to } = useMemo(() => {
    const def = QUARTERS.find((q) => q.value === quarter) ?? QUARTERS[0]!
    const fromStr = fmt(year, def.fromMonth, 1)
    const toStr = fmt(year, def.toMonth, lastDayOfMonth(year, def.toMonth + 1))
    return { from: fromStr, to: toStr }
  }, [year, quarter])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ from, to })
      if (serviceLine) params.set('serviceLine', serviceLine)
      const data = await api.get<PnlReport>(`/accounting/reports/pnl?${params.toString()}`)
      setReport(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('errors.loadFailed')
      setError(text)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [from, to, serviceLine, t])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-toga-500/15 text-toga-700 dark:text-toga-300">
          <BookOpen size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold">{t('title')}</h1>
          <p className="text-sm t-muted">
            {t('subtitle')}
          </p>
        </div>
      </header>

      {/* Selectores */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <div>
          <label className="block text-xs font-medium t-muted mb-1">{t('filters.year')}</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium t-muted mb-1">{t('filters.period')}</label>
          <select
            value={quarter}
            onChange={(e) => setQuarter(e.target.value as QuarterMode)}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
          >
            {QUARTERS.map((q) => (
              <option key={q.value} value={q.value}>
                {t(q.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium t-muted mb-1">{t('filters.serviceLine')}</label>
          <select
            value={serviceLine}
            onChange={(e) => setServiceLine(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
          >
            <option value="">{t('serviceLines.all')}</option>
            {lines.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs t-muted ml-auto">
          {t('rangeLabel', { from, to })}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm t-muted">
          <Loader2 size={16} className="animate-spin" />
          {t('loading')}
        </div>
      )}

      {report && !loading && (
        <div className="overflow-hidden rounded-xl border border-[var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-input-soft)] t-muted">
              <tr>
                <th className="text-left px-4 py-2 font-medium">{t('table.accountBlock')}</th>
                <th className="text-right px-4 py-2 font-medium">{t('table.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {report.sections.map((sec) => {
                const isHighlighted = HIGHLIGHTED_KEYS.has(sec.key)
                return (
                  <SectionRows key={sec.key} section={sec} highlighted={isHighlighted} />
                )
              })}
            </tbody>
          </table>

          {/* Comparativa */}
          <div className="bg-[var(--bg-input-soft)] border-t border-[var(--border)] px-4 py-3 flex justify-between text-sm">
            <span className="t-muted">
              {t('previousYearResult')}
            </span>
            <span className="font-mono">
              {report.comparisons.previousYear
                ? formatCents(report.comparisons.previousYear.finalResult)
                : '—'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

function SectionRows({
  section,
  highlighted,
}: {
  section: PnlSection
  highlighted: boolean
}) {
  return (
    <>
      <tr
        className={
          highlighted
            ? 'bg-[var(--bg-hover)] border-t border-[var(--border)] t-primary'
            : 'bg-[var(--bg-input-soft)] border-t border-[var(--border)]'
        }
      >
        <td className={highlighted ? 'px-4 py-2 font-semibold' : 'px-4 py-2 font-medium'}>
          {section.title}
        </td>
        <td
          className={
            highlighted
              ? 'px-4 py-2 text-right font-mono font-semibold'
              : 'px-4 py-2 text-right font-mono'
          }
        >
          {formatCents(section.subtotal)}
        </td>
      </tr>
      {section.accounts.map((a) => (
        <tr key={`${section.key}-${a.code}`} className="border-t border-[var(--border)]/60">
          <td className="px-4 py-1.5 pl-10 t-secondary">
            <span className="t-muted mr-2">{a.code}</span>
            {a.name}
          </td>
          <td className="px-4 py-1.5 text-right font-mono t-secondary">
            {formatCents(a.amount)}
          </td>
        </tr>
      ))}
    </>
  )
}
